import {useTranslation} from 'react-i18next';
import {RoomDetailsCardBottomProps} from './types';

import {
  AttendeeAvailabilityStatus,
  formatDuration,
  getDynamicFreeTimeslot,
  getIsOnlineMeetingLinkSupported,
  getMeetingRoomSuggestionDataById,
  markAsBookedLocally,
} from '@lib/store';
import {useAppSelector, useCreateEvent, useFormatDate} from '@hooks';

import {FlexCol, FlexRow, P, Span} from '@quarks';
import {ModalPageBottom} from '@organisms';
import {Button} from '@molecules';
import {StyledContainer, StyledText} from './styles';
import {intervalToDuration} from 'date-fns';
import {stripSecondsAndMilliseconds} from '@lib/utils';
import {useDispatch} from 'react-redux';

export const MeetingRoomDetailsCardBottom = ({
  room,
  startDateTime,
  duration,
  onBookingDetailsPress,
  onBookNowCallback,
}: RoomDetailsCardBottomProps) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {start: dynamicStart, end: dynamicEnd} =
    useAppSelector((state) =>
      getDynamicFreeTimeslot(state, room.buildingId, room.id, startDateTime, {minutes: duration}),
    ) ?? {};
  const {markedAsBookedLocally, scheduleAvailability} =
    useAppSelector((state) => getMeetingRoomSuggestionDataById(state, room.buildingId, room.suggestionData ?? '')) ??
    {};

  const onlineMeetingLinkSupported = useAppSelector(getIsOnlineMeetingLinkSupported);

  const dynamicTimeslotFound = !!dynamicStart && !!dynamicEnd;

  const dynamicStartString = dynamicStart && dynamicStart.toISOString();
  const dynamicEndString = dynamicEnd && dynamicEnd.toISOString();

  const start = useFormatDate(dynamicStartString ?? '', 'HH:mm');
  const end = useFormatDate(dynamicEndString ?? '', 'HH:mm');

  const handleOnSuccess = () => {
    dispatch(markAsBookedLocally({buildingId: room.buildingId, roomId: room.id}));
    onBookNowCallback && onBookNowCallback();
  };

  const {createEvent, isCreatingEvent} = useCreateEvent(handleOnSuccess);

  const durationFormated =
    dynamicStart && dynamicEnd
      ? intervalToDuration({
          start: stripSecondsAndMilliseconds(dynamicStart),
          end: dynamicEnd,
        })
      : undefined;

  const durationAsString = formatDuration(t, durationFormated, false);

  const havePermissionForRoom = scheduleAvailability && scheduleAvailability !== AttendeeAvailabilityStatus.Unknown;
  const canBook = dynamicTimeslotFound && !markedAsBookedLocally && havePermissionForRoom;

  return (
    <StyledContainer gap={16}>
      {canBook ? (
        <FlexCol
          justifyContent="center"
          gap={8}
          alignItems="flex-start">
          <FlexRow>
            <P>
              <Span fontWeight="bold">{`${start} - ${end} `}</Span>
              {`(${durationAsString})`}
            </P>
          </FlexRow>
          <Button
            onClick={onBookingDetailsPress}
            loading={isCreatingEvent}
            button={'text'}>
            {t('translation:BookingDetails')}
          </Button>
        </FlexCol>
      ) : havePermissionForRoom ? (
        <StyledText alignSelf="center">{t('hereAndNow:RoomNotAvailable')}</StyledText>
      ) : (
        <P alignSelf="center">{t('hereAndNow:NoPermission')}</P>
      )}
      <ModalPageBottom
        buttonLabel={t('translation:BookNow')}
        buttonLoading={isCreatingEvent}
        buttonDisabled={!canBook}
        style={{padding: '0'}}
        onClick={() =>
          createEvent(
            {
              title: room.displayName,
              message: '',
              attendees: [],
              startDateTime: dynamicStartString ?? '',
              endDateTime: dynamicEndString ?? '',
              isOnlineMeeting: onlineMeetingLinkSupported,
              rooms: [room.email],
              isAllDay: false,
              organizerTimeZone: '',
              origin: 'BookRoomNow',
            },
            t('hereAndNow:RoomBookingSuccessMessage'),
          )
        }
      />
    </StyledContainer>
  );
};
